// src/BlogDetails.js

import React, { useState, useEffect, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { IMAGES } from "../assets/img/ImageIndex";
import axios from "axios";
import BlogSidebar from "../components/BlogSidebar";
import BlogDetailsSkeleton from "../components/BlogDetailsSkeleton";
import BlogHeaderSkeleton from "../components/BlogHeaderSkeleton";

function BlogDetails() {
    const { slug } = useParams();
    const [blog, setBlog] = useState(null);
    const [previousPost, setPreviousPost] = useState(null);
    const [nextPost, setNextPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [imageLoading, setImageLoading] = useState(false);

    const fetchBlogDetails = useCallback(async () => {
        if (!slug) {
            return;
        }

        try {
            setLoading(true);
            const API_BASE_URL = process.env.REACT_APP_BASE_URL || 'https://cspv0tmvph.execute-api.us-east-1.amazonaws.com/production';

            // Fetch blog details
            const response = await axios.get(`${API_BASE_URL}/get-blog-by-slug?slug=${slug}`);
            const blogData = response.data.data;
            setBlog(blogData);

            // Store slugs for navigation but don't fetch full data yet
            setPreviousPost(blogData.previous_slug ? { slug: blogData.previous_slug } : null);
            setNextPost(blogData.next_slug ? { slug: blogData.next_slug } : null);
        } catch (error) {
            console.error('Error fetching blog details:', error);
            setBlog(null);
            setPreviousPost(null);
            setNextPost(null);
        } finally {
            setLoading(false);
        }
    }, [slug]);

    const fetchAdjacentPostData = useCallback(async (postSlug) => {
        if (!postSlug) return null;

        try {
            const API_BASE_URL = process.env.REACT_APP_BASE_URL || 'https://cspv0tmvph.execute-api.us-east-1.amazonaws.com/production';
            const response = await axios.get(`${API_BASE_URL}/get-blog-by-slug?slug=${postSlug}`);
            return response.data.data;
        } catch (error) {
            console.error('Error fetching adjacent post:', error);
            return null;
        }
    }, []);

    const handlePreviousClick = async () => {
        if (previousPost && !previousPost.title) {
            const postData = await fetchAdjacentPostData(previousPost.slug);
            if (postData) {
                setPreviousPost(postData);
            }
        }
    };

    const handleNextClick = async () => {
        if (nextPost && !nextPost.title) {
            const postData = await fetchAdjacentPostData(nextPost.slug);
            if (postData) {
                setNextPost(postData);
            }
        }
    };

    useEffect(() => {
        fetchBlogDetails();
    }, [fetchBlogDetails]);

    useEffect(() => {
        if (blog) {
            // Set document title
            document.title = blog.meta_title || blog.title || 'Blog Details';

            // Set meta description
            const metaDescription = document.querySelector('meta[name="description"]');
            if (metaDescription) {
                metaDescription.setAttribute('content', blog.meta_description || blog.title || 'Read our latest blog post');
            }
            const metaTitle = document.querySelector('meta[name="title"]');
            if (metaTitle) {
                metaTitle.setAttribute('content', blog.meta_title || blog.title || 'Read our latest blog post');
            }
        }
    }, [blog]);

    const handleImageLoad = () => {
        setImageLoading(false);
    };

    const handleImageError = () => {
        setImageLoading(false);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    };

    const createMarkup = (htmlContent) => {
        return { __html: htmlContent };
    };
    return (
        <div className="loan-program-page">
            {loading ? (
                <BlogHeaderSkeleton />
            ) : (
                <div className="page-banner-area bg-gray2">
                    <div className="container mw-1380">
                        <div className="row align-items-center">
                            <div className="col-lg-6">
                                <div className="page-banner-content">
                                    <ul className="ps-0 list-unstyled breadcrumbs">
                                        <li><Link to="/">Home</Link></li>
                                        <li><Link to="/blog">Blog</Link></li>
                                        <li>{blog ? blog.title : 'Blog Details'}</li>
                                    </ul>
                                    <h1>{blog ? blog.title : 'Blog Details'}</h1>
                                </div>
                            </div>
                            <div className="col-lg-6">
                                <div className="page-banner-img text-center text-lg-end position-relative z-1">
                                    <img src={IMAGES.shape7} className="shape shape-7" alt="Shape1" />
                                    <img src={IMAGES.shape8} className="shape shape-8" alt="Shape2" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <div className="blog-page-area ptb-130">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-8 col-md-12">
                            {loading ? (
                                <BlogDetailsSkeleton />
                            ) : blog ? (
                                <>
                                    <div className="blog-details blog-left-sidebar">
                                        <div className="blog-details-desc">
                                            <div className="image">
                                                {blog.image_path ? (
                                                    <>
                                                        {imageLoading && (
                                                            <div className="skeleton-loader" style={{ height: '400px', backgroundColor: '#f0f0f0', animation: 'pulse 1.5s ease-in-out infinite' }}></div>
                                                        )}
                                                        <img
                                                            src={blog.image_path}
                                                            alt={blog.title}
                                                            style={{ display: imageLoading ? 'none' : 'block' }}
                                                            onLoad={handleImageLoad}
                                                            onError={handleImageError}
                                                        />
                                                    </>
                                                ) : (
                                                    <img src={IMAGES.imageNotFound} alt={blog.title} />
                                                )}
                                            </div>
                                            <ul className="ps-0 mb-0 list-unstyled d-flex flex-wrap blog-info">
                                                <li>
                                                    <Link>
                                                        <i className="ri-user-3-line"></i>
                                                        <span>Admin</span>
                                                    </Link>
                                                </li>
                                                <li>
                                                    <Link>
                                                        <i className="ri-calendar-2-line"></i>
                                                        <span>{formatDate(blog.created_at)}</span>
                                                    </Link>
                                                </li>
                                            </ul>
                                        </div>
                                        <div className="blog-details-content insights-details-content">
                                            <h2 className="wp-block-heading">{blog.title}</h2>
                                            <div dangerouslySetInnerHTML={createMarkup(blog.content)}></div>

                                            <div className="prev-next-btn">
                                                <nav className="navigation post-navigation" aria-label="Posts">
                                                    <h2 className="screen-reader-text">Blog navigation</h2>
                                                    <div className="nav-links">
                                                        <div className="nav-previous">
                                                            {previousPost ? (
                                                                <Link
                                                                    to={`/blog-details/${previousPost.slug}`}
                                                                    rel="prev"
                                                                    onClick={handlePreviousClick}
                                                                >
                                                                    <p className="meta-nav">
                                                                        <i className="ri-arrow-left-line"></i>
                                                                        {previousPost.title || 'Previous Blog'}
                                                                    </p>
                                                                </Link>
                                                            ) : (
                                                                <Link to="/blog" rel="prev">
                                                                    <p className="meta-nav"><i className="ri-arrow-left-line"></i>Back to Blog</p>
                                                                </Link>
                                                            )}
                                                        </div>
                                                        <div className="nav-next">
                                                            {nextPost ? (
                                                                <Link
                                                                    to={`/blog-details/${nextPost.slug}`}
                                                                    rel="next"
                                                                    onClick={handleNextClick}
                                                                >
                                                                    <p className="meta-nav">
                                                                        {nextPost.title || 'Next Blog'}
                                                                        <i className="ri-arrow-right-line"></i>
                                                                    </p>
                                                                </Link>
                                                            ) : (
                                                                <Link to="/blog" rel="next">
                                                                    <p className="meta-nav">Back to Blog<i className="ri-arrow-right-line"></i></p>
                                                                </Link>
                                                            )}
                                                        </div>
                                                    </div>
                                                </nav>
                                            </div>
                                        </div>
                                    </div>
                                    {/* <div id="comments" className="comments-area">
                                        <div id="respond" className="comment-respond">
                                            <h3 id="reply-title" className="comment-reply-title">Leave a Reply
                                                <small>
                                                    <Link to="#respond" style={{ display: 'none' }}>Cancel reply</Link>
                                                </small>
                                            </h3>
                                            <form action="#" method="post" id="commentform" className="comment-form" noValidate="">
                                                <p className="comment-notes">
                                                    <span id="email-notes">Your email address will not be published.</span>
                                                    <span className="required-field-message">Required fields are marked
                                                        <span className="required">*</span>
                                                    </span>
                                                </p>
                                                <p className="comment-form-comment">
                                                    <label for="comment">Comment <span className="required">*</span></label>
                                                    <textarea placeholder="Type Your Comments" id="comment" name="comment" cols="45" rows="8" maxLength="65525" required=""></textarea>
                                                </p>
                                                <p className="comment-form-author">
                                                    <label for="author">Name <span className="required">*</span></label>
                                                    <input placeholder="Name" id="author" name="author" type="text" value="" size="30" maxLength="245" autocomplete="name" required="" />
                                                </p>
                                                <p className="comment-form-email">
                                                    <label for="email">Email <span className="required">*</span></label>
                                                    <input placeholder="Email" id="email" name="email" type="email" value="" size="30" maxLength="100" aria-describedby="email-notes" autocomplete="email" required="" />
                                                </p>
                                                <p className="comment-form-url">
                                                    <label for="url">Website</label>
                                                    <input placeholder="Website" id="url" name="url" type="url" value="" size="30" maxLength="200" autocomplete="url" />
                                                </p>
                                                <p className="comment-form-cookies-consent">
                                                    <input id="wp-comment-cookies-consent" name="wp-comment-cookies-consent" type="checkbox" value="yes" />
                                                    <label for="wp-comment-cookies-consent">Save my name, email, and website in this browser for the next time I comment.</label>
                                                </p>
                                                <p className="form-submit"><input name="submit" type="submit" id="submit" className="submit" value="Post a Comment" />
                                                    <input type="hidden" name="comment_post_ID" value="641" id="comment_post_ID" />
                                                    <input type="hidden" name="comment_parent" id="comment_parent" value="0" />
                                                </p>
                                            </form>
                                        </div>
                                    </div> */}
                                </>
                            ) : (
                                <div className="blog-details blog-left-sidebar">
                                    <div className="text-center py-5">
                                        <p>Blog not found.</p>
                                        <Link to="/blog" className="btn btn-primary">Back to Blog</Link>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="col-lg-4 col-md-12">
                            <BlogSidebar />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default BlogDetails;
